import { Inject, Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, filter, map } from "rxjs/operators";
import { PATHS, ROUTES } from "src/constants";
import { Location } from "@angular/common";
import { HttpClient } from "@angular/common/http";

export type Language = "en" | "de";

@Injectable({
  providedIn: "root",
})
export class LanguageService {
  private currentLanguageSubject = new BehaviorSubject<Language>("en");
  public currentLanguage$: Observable<Language> = this.currentLanguageSubject.asObservable();

  // Map English routes to German routes
  private routeMappings = {
    en: {
      [PATHS[ROUTES.HOME_DE]]: "",
      en: "",
      [PATHS[ROUTES.WHAT_DE]]: PATHS[ROUTES.WHAT],
      [PATHS[ROUTES.WORKS_DE]]: PATHS[ROUTES.WORKS],
      [PATHS[ROUTES.NEARSHORING_DE]]: PATHS[ROUTES.NEARSHORING_EN],
    },
    de: {
      [PATHS[ROUTES.HOME]]: PATHS[ROUTES.HOME_DE],
      "": PATHS[ROUTES.HOME_DE],
      en: PATHS[ROUTES.HOME_DE],
      [PATHS[ROUTES.WHAT]]: PATHS[ROUTES.WHAT_DE],
      [PATHS[ROUTES.WORKS]]: PATHS[ROUTES.WORKS_DE],
      [PATHS[ROUTES.NEARSHORING_EN]]: PATHS[ROUTES.NEARSHORING_DE],
    },
  };

  constructor(
    private router: Router,
    private location: Location,
    private http: HttpClient,
    @Inject("isBrowser") private isBrowser: boolean,
  ) {
    this.detectInitialLanguage();

    // Subscribe to route changes to update language based on URL
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.detectLanguageFromUrl();
    });
  }

  private detectInitialLanguage(): void {
    if (!this.isBrowser) {
      return;
    }

    // First check URL for language indicators
    if (this.isGermanUrl(this.router.url)) {
      this.setLanguage("de");
      return;
    }

    // Then check browser language
    const browserLang = navigator.language || (navigator as any).userLanguage;
    if (browserLang && browserLang.toLowerCase().startsWith("de")) {
      this.setLanguage("de");
      return;
    }

    // Check for German IP address
    this.detectGermanIp().subscribe(isGermanIp => {
      if (isGermanIp) {
        this.setLanguage("de");
      } else {
        // Default to English
        // this.setLanguage("en");
      }
    });
  }

  public detectLanguageFromUrl(): void {
    const isGermanUrl = this.isGermanUrl(this.router.url);
    this.setLanguage(isGermanUrl ? "de" : "en");
  }

  private isGermanUrl(url: string): boolean {
    // Check if the URL contains German-specific paths
    // First, extract the path from the URL
    const path = url.split("/")[1] || "";

    // Then check if it matches any German paths
    return (
      path === PATHS[ROUTES.HOME_DE] ||
      path === PATHS[ROUTES.WHAT_DE] ||
      path === PATHS[ROUTES.WORKS_DE] ||
      path === PATHS[ROUTES.NEARSHORING_DE]
    );
  }

  private isEnglishUrl(url: string): boolean {
    // Check if the URL contains English-specific paths
    // First, extract the path from the URL
    const path = url.split("/")[1] || "";

    // Empty path is English home
    if (path === "" || path === "en") {
      return true;
    }

    // Then check if it matches any English paths
    return (
      path === PATHS[ROUTES.WHAT] ||
      path === PATHS[ROUTES.WORKS] ||
      path === PATHS[ROUTES.NEARSHORING] ||
      path === PATHS[ROUTES.BLOG] ||
      path === PATHS[ROUTES.INCUBATOR]
    );
  }

  public setLanguage(language: Language): void {
    if (this.currentLanguageSubject.value !== language) {
      this.currentLanguageSubject.next(language);
      this.updateHtmlLang(language);

      // Store the language preference in localStorage
      if (this.isBrowser) {
        localStorage.setItem("preferredLanguage", language);
      }
    }
  }

  public toggleLanguage(): void {
    const currentLang = this.currentLanguageSubject.value;
    const newLang = currentLang === "en" ? "de" : "en";
    this.setLanguage(newLang);
    this.navigateToTranslatedRoute(newLang);
  }

  private updateHtmlLang(language: Language): void {
    if (this.isBrowser) {
      document.documentElement.lang = language;
    }
  }

  public getCurrentLanguage(): Language {
    return this.currentLanguageSubject.value;
  }

  public navigateToTranslatedRoute(language: Language): void {
    const currentUrl = this.router.url;
    const currentPath = currentUrl.split("/")[1] || "";
    const queryParams = this.getQueryParamsFromUrl(currentUrl);
    const fragment = this.getFragmentFromUrl(currentUrl);

    // Special handling for home page to prevent redirect loop
    if (currentPath === "" || currentPath === "en" || currentPath === PATHS[ROUTES.HOME_DE]) {
      // For the root path or German home, navigate directly without using the redirector
      const homePath = language === "en" ? "" : PATHS[ROUTES.HOME_DE];
      this.router.navigate([homePath], {
        queryParams: queryParams,
        fragment: fragment,
        replaceUrl: true, // Use replaceUrl to prevent building up history stack
      });
      return;
    }

    // Check if we have a mapping for this route
    const mappings = this.routeMappings[language];

    if (mappings && mappings[currentPath]) {
      const newPath = mappings[currentPath];
      const newUrl = `/${newPath}${this.router.url.substring(currentPath.length + 1)}`;
      this.router.navigateByUrl(newUrl);
    } else if (currentPath === PATHS[ROUTES.BLOG] || currentPath === PATHS[ROUTES.INCUBATOR]) {
      // For routes that don't have translations, just stay on current page
      // but update the language state
    } else {
      // Log which path wasn't found in the mappings for debugging
      console.log(`No mapping found for path: ${currentPath} in language: ${language}`);

      // Direct routing based on known paths
      if (language === "de") {
        if (currentPath === PATHS[ROUTES.WHAT]) {
          this.router.navigate([PATHS[ROUTES.WHAT_DE]], { queryParams, fragment });
          return;
        } else if (currentPath === PATHS[ROUTES.WORKS]) {
          this.router.navigate([PATHS[ROUTES.WORKS_DE]], { queryParams, fragment });
          return;
        } else if (currentPath === PATHS[ROUTES.NEARSHORING]) {
          this.router.navigate([PATHS[ROUTES.NEARSHORING_DE]], { queryParams, fragment });
          return;
        }
      } else if (language === "en") {
        if (currentPath === PATHS[ROUTES.WHAT_DE]) {
          this.router.navigate([PATHS[ROUTES.WHAT]], { queryParams, fragment });
          return;
        } else if (currentPath === PATHS[ROUTES.WORKS_DE]) {
          this.router.navigate([PATHS[ROUTES.WORKS]], { queryParams, fragment });
          return;
        } else if (currentPath === PATHS[ROUTES.NEARSHORING_DE]) {
          this.router.navigate([PATHS[ROUTES.NEARSHORING]], { queryParams, fragment });
          return;
        }
      }

      // Fallback to home page if no mapping exists
      const homePath = language === "en" ? "" : PATHS[ROUTES.HOME_DE];
      this.router.navigate([homePath], {
        queryParams: queryParams,
        fragment: fragment,
        replaceUrl: true, // Use replaceUrl to prevent building up history stack
      });
    }
  }

  private getQueryParamsFromUrl(url: string): { [key: string]: string } {
    const queryParams = {};
    const queryIndex = url.indexOf("?");

    if (queryIndex !== -1) {
      const queryString = url.substring(queryIndex + 1).split("#")[0];
      const params = queryString.split("&");

      params.forEach(param => {
        const [key, value] = param.split("=");
        if (key && value) {
          queryParams[key] = value;
        }
      });
    }

    return queryParams;
  }

  private getFragmentFromUrl(url: string): string | null {
    const fragmentIndex = url.indexOf("#");
    return fragmentIndex !== -1 ? url.substring(fragmentIndex + 1) : null;
  }

  /**
   * Detects if the user is accessing the site from a German IP address
   * Uses a free IP geolocation API
   */
  private detectGermanIp(): Observable<boolean> {
    if (!this.isBrowser) {
      return of(false);
    }

    return this.http.get<any>("https://ipapi.co/json/").pipe(
      map(response => {
        // Check if the country code is Germany (DE)
        return response && response.country === "DE";
      }),
      catchError(error => {
        console.error("Error detecting IP location:", error);
        return of(false);
      }),
    );
  }

  /**
   * Gets user's preferred language based on all available signals
   * Priority: 1. URL path, 2. Stored preference, 3. Browser language, 4. IP location
   */
  public getPreferredLanguage(): Observable<Language> {
    if (this.isGermanUrl(this.router.url)) {
      return of("de");
    }

    // Check local storage for saved preference
    if (this.isBrowser) {
      const storedLang = localStorage.getItem("preferredLanguage") as Language;
      if (storedLang && (storedLang === "en" || storedLang === "de")) {
        return of(storedLang);
      }
    }

    // Check browser language
    if (this.isBrowser) {
      const browserLang = navigator.language || (navigator as any).userLanguage;
      if (browserLang && browserLang.toLowerCase().startsWith("de")) {
        return of("de");
      }
    }

    // Check IP location as last resort
    return this.detectGermanIp().pipe(map(isGermanIp => (isGermanIp ? "de" : "en")));
  }
}
